import {
  ActionIcon,
  Anchor,
  Divider,
  Group,
  Indicator,
  LoadingOverlay,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { getProfile, updateProfile, updateSelf } from "../../../api/UserAPI";
import { useProfileStore } from "../../../stores/UserStore";
import {
  IconPin,
  IconPinEnd,
  IconPinInvoke,
  IconShare3,
  IconTrash,
} from "@tabler/icons-react";
import { useLanguage } from "../../../stores/LanguageStore";
import { filterNumberTypes } from "../filterSettings";
import { LinearProgress } from "@mui/material";
import { notifications } from "@mantine/notifications";
import PopOver from "../../View/PopOver";
import FilterListSharing from "../FilterListSharing";
import { filterByCreatedByNull } from "../../../functions/utilFunctions";

const FilterListMobile = ({
  pageName,
  handleFilter,
  loading,
  setLoading,
  fields,
  savedFiltersReadonly,
}) => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);

  const filters = useMemo(() => {
    return profile?.saved_filters?.[pageName]?.map((item, index) => ({
      ...item,
      id: index,
    }));
  }, [pageName, profile]);

  const handleFilterImport = (f) => {
    let newFilter = {
      mode: f?.mode,
      values: f?.values?.map((item) => {
        let allFieldsOptions = fields.reduce((acc, v) => [...v.options], []);
        let isNumber =
          allFieldsOptions.find((x) => x.id === item.field_name)?.type ===
          "number";

        return {
          ...item,
          type: isNumber
            ? Object.keys(filterNumberTypes).find(
                (key) => filterNumberTypes[key] === item.type
              )
            : item?.type,
        };
      }),
    };

    handleFilter(newFilter);
  };

  const [deleting, setDeleting] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [pinningToolbar, setPinningToolbar] = useState(false);
  const handleDeleteItem = (index) => {
    setDeleting(index);
    getProfile().then((res) => {
      let profile = res.data.user_profile;
      let filterSector = [...profile.saved_filters[pageName]];
      filterSector.splice(index, 1);

      const newData = {
        saved_filters: {
          ...profile.saved_filters,
          [pageName]: filterSector,
        },
      };

      const finalData = {
        saved_filters: filterByCreatedByNull(newData.saved_filters),
      };

      updateProfile(null, finalData).then((response) => {
        updateSelf()
          .then(() => {
            notifications.show({
              message: lang.table.toolbar.filter.filter_pinned_deleted,
              color: "tertiary.8",
            });
          })
          .finally(() => setDeleting(false));
      });
    });
  };

  const handlePin = (index) => {
    setPinning(index);
    getProfile().then((res) => {
      let profile = res.data.user_profile;
      let filterSector = [...profile.saved_filters[pageName]];

      let isPinned = false;

      let newFilterSector = filterSector.map((f, i) => {
        if (i === index) {
          if (f?.pinned) {
            isPinned = true;
          }
          return {
            ...f,
            pinned: !f?.pinned,
          };
        }
        return {
          ...f,
          pinned: false,
        };
      });

      const newData = {
        saved_filters: {
          ...profile.saved_filters,
          [pageName]: newFilterSector,
        },
      };

      const finalData = {
        saved_filters: filterByCreatedByNull(newData.saved_filters),
      };

      updateProfile(null, finalData).then((response) => {
        updateSelf()
          .then(() => {
            notifications.show({
              message: isPinned
                ? lang.table.toolbar.filter.unpinned
                : lang.table.toolbar.filter.pinned,
              color: "tertiary.8",
            });
          })
          .finally(() => setPinning(false));
      });
    });
  };

  const handlePinToolbar = (index) => {
    setPinningToolbar(index);
    getProfile().then((res) => {
      let profile = res.data.user_profile;
      let filterSector = [...profile.saved_filters[pageName]];

      let isPinned = false;

      let newFilterSector = filterSector.map((f, i) => {
        if (i === index) {
          if (f?.pinnedToToolbar) {
            isPinned = true;
          }
          return {
            ...f,
            pinnedToToolbar: !f?.pinnedToToolbar,
          };
        }
        return f;
      });
      const newData = {
        saved_filters: {
          ...profile.saved_filters,
          [pageName]: newFilterSector,
        },
      };

      const finalData = {
        saved_filters: filterByCreatedByNull(newData.saved_filters),
      };

      updateProfile(null, finalData).then((response) => {
        updateSelf()
          .then(() => {
            notifications.show({
              message: isPinned
                ? lang.table.toolbar.filter.unpinned
                : lang.table.toolbar.filter.pinned,
              color: "tertiary.8",
            });
          })
          .finally(() => setPinningToolbar(false));
      });
    });
  };

  useEffect(() => {
    if (loading) updateSelf().finally(() => setLoading(false));
  }, [loading]);

  return (
    <>
      <Divider orientation="vertical" />
      <Stack
        pt={3}
        pos={"relative"}
        flex={savedFiltersReadonly ? 1 : undefined}
        gap={0}
      >
        {filters?.length > 0 ? (
          <>
            {loading && (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                }}
              />
            )}
            {filters?.map((filterItem, index) => {
              let fromSharing =
                filterItem?.created_by?.id &&
                filterItem?.created_by?.id !==
                  useProfileStore.getState().profile?.id;
              return (
                <Group
                  key={filterItem?.id}
                  wrap="nowrap"
                  gap={0}
                  style={{
                    borderBottom: "1px solid #0000000a",
                    borderTop: index === 0 ? "1px solid #0000000a" : undefined,
                  }}
                  py={4}
                >
                  <Anchor
                    fw={600}
                    truncate="end"
                    mr={4}
                    size="md"
                    onClick={() => handleFilterImport(filterItem?.filter)}
                  >
                    {filterItem?.name}
                  </Anchor>
                  {/* <Tooltip label={lang.table.toolbar.filter.import}>
                  <ActionIcon
                    variant="light"
                    onClick={() => handleFilterImport(filterItem?.filter)}
                    ml={"auto"}
                    size={"sm"}
                    color="tertiary.8"
                    disabled={deleting !== false || pinning !== false}
                  >
                    <IconUpload size={14} />
                  </ActionIcon>
                </Tooltip> */}

                  {!savedFiltersReadonly && (
                    <>
                      <Tooltip
                        label={
                          filterItem?.pinned
                            ? lang.table.toolbar.filter.unpin
                            : lang.table.toolbar.filter.pin
                        }
                      >
                        <ActionIcon
                          variant={filterItem?.pinned ? "filled" : "subtle"}
                          onClick={() => handlePin(index)}
                          ml={"auto"}
                          loading={pinning === index}
                          disabled={
                            deleting !== false ||
                            pinning !== false ||
                            fromSharing
                          }
                        >
                          <IconPin size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                </Group>
              );
            })}
          </>
        ) : loading ? (
          <LoadingOverlay visible loaderProps={{ size: 24 }} />
        ) : (
          <Text size="sm" opacity={0.5}>
            {lang.table.toolbar.filter.no_pinned_filters}
          </Text>
        )}
      </Stack>
    </>
  );
};

export default FilterListMobile;
